import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Collapse } from "../collapse";
import { Divider } from "../divider";
import { ScrollArea } from "../scroll-area";
import { Skeleton } from "../skeleton";
import { Stack } from "../stack";
import { Text } from "../text";
import { GroupedListGroupHeader } from "./grouped-list-group-header";
import { GroupedListOption } from "./grouped-list-option";
import { GROUPED_LIST_LOADING_ROW_COUNT } from "./grouped-list.constants";
import { createGenericComponentWrapper } from "../../utilities/component-wrapper/component-wrapper";
import type { CustomGroupedListFactory, IProps } from "./grouped-list.types";
import { getGroupHeaderId, getOptionId, getSelectableItems } from "./grouped-list.utilities";
import styles from "./grouped-list.module.scss";

// Create a generic component wrapper instance, as AutoTable does.
const genericComponentWrapper = createGenericComponentWrapper();

/**
 * A list of items under collapsible group headers, for a picker whose input keeps focus: an editor, or a search above
 * the list. Each group has a `GroupedList.GroupHeader` (its label, count and a collapse chevron) over a
 * `GroupedList.Option` per item (a title, an optional subtitle and a description), both styled as menu items.
 * - Focus never moves into the list. The input forwards its keys through the ref's `onKeyDown`: the arrows move the
 *   active option (skipping collapsed groups, wrapping at either end, and scrolling it into view) and Enter chooses it.
 *   The pointer moves the active option too, so only one is ever marked. `onActiveOptionChange` reports the active
 *   option's element id, for the input's `aria-activedescendant`.
 * - Every group starts expanded on each mount, so collapsing lasts until the list unmounts. A collapsed group keeps its
 *   header and count.
 * - While loading, placeholder rows stand in for the list; with no groups, `emptyMessage` does.
 * - Its height is capped in its stylesheet to the room its popover reports beside the target (see the module).
 * @template Item - The type of the listed items.
 * @returns A `GroupedList` typed for `Item`, with `GroupHeader` and `Option` as static sub-components.
 */
function createGroupedList<Item = any>() {
    const GroupedListComponent = genericComponentWrapper<CustomGroupedListFactory<Item>, IProps<Item>>(
        "GroupedList",
        (
            {
                id,
                label,
                groups,
                getItemId,
                getOptionContent,
                onChoose,
                onActiveOptionChange,
                resetKey,
                isLoading = false,
                emptyMessage,
                isFocusable = false,
            },
            ref
        ) => {
            const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(() => new Set());
            const [activeItemId, setActiveItemId] = useState<string | null>(null);
            // Set by the arrows: only a keyboard move scrolls (scrolling on hover would move options under the
            // pointer) and rings the active option, as the Select rings its keyboard-highlighted option.
            const [isKeyboardActive, setIsKeyboardActive] = useState(false);
            const shouldScrollRef = useRef(false);

            const selectable = useMemo(
                () => getSelectableItems(groups, collapsedGroupIds),
                [collapsedGroupIds, groups]
            );

            // A new reset key (e.g. a new search) starts again from the first option.
            useEffect(() => setActiveItemId(null), [resetKey]);

            // The active option is the one last moved to, while it is still on show; otherwise the first.
            const activeIndex = useMemo(
                () =>
                    Math.max(
                        0,
                        selectable.findIndex((item) => getItemId(item) === activeItemId)
                    ),
                [activeItemId, getItemId, selectable]
            );
            const activeItem: Item | undefined = selectable[activeIndex];
            const activeOptionId = typeof activeItem === "undefined" ? null : getOptionId(id, getItemId(activeItem));

            useEffect(() => onActiveOptionChange?.(activeOptionId), [activeOptionId, onActiveOptionChange]);

            // An unmounted list has no active option, so the next one never starts on this one's.
            useEffect(() => () => onActiveOptionChange?.(null), [onActiveOptionChange]);

            useEffect(() => {
                if (shouldScrollRef.current && activeOptionId !== null) {
                    // `scrollIntoView` is missing in some environments (jsdom).
                    document.getElementById(activeOptionId)?.scrollIntoView?.({ block: "nearest" });
                }
                shouldScrollRef.current = false;
            }, [activeOptionId]);

            useImperativeHandle(
                ref,
                () => ({
                    onKeyDown: ({ key, shiftKey, metaKey, ctrlKey, altKey }) => {
                        if (shiftKey || metaKey || ctrlKey || altKey) {
                            return false;
                        }
                        if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "Enter") {
                            return false;
                        }
                        // Held even with nothing to choose, so Enter never falls through to the input (e.g. to send).
                        if (typeof activeItem === "undefined") {
                            return true;
                        }
                        if (key === "Enter") {
                            onChoose(activeItem);
                            return true;
                        }
                        const step = key === "ArrowDown" ? 1 : -1;
                        const nextIndex = (activeIndex + step + selectable.length) % selectable.length;
                        // A move to the same option (a list of one) changes nothing, so the scroll effect would not
                        // reset the flag.
                        shouldScrollRef.current = nextIndex !== activeIndex;
                        setIsKeyboardActive(true);
                        setActiveItemId(getItemId(selectable[nextIndex]));
                        return true;
                    },
                }),
                [activeIndex, activeItem, getItemId, onChoose, selectable]
            );

            const toggleGroup = useCallback((groupId: string) => {
                setCollapsedGroupIds((current) => {
                    const next = new Set(current);
                    if (next.has(groupId)) {
                        next.delete(groupId);
                    } else {
                        next.add(groupId);
                    }
                    return next;
                });
            }, []);

            const itemsById = useMemo(
                () => new Map(groups.flatMap(({ items }) => items.map((item) => [getItemId(item), item] as const))),
                [getItemId, groups]
            );

            const chooseItem = useCallback(
                (itemId: string) => {
                    const item = itemsById.get(itemId);
                    if (typeof item !== "undefined") {
                        onChoose(item);
                    }
                },
                [itemsById, onChoose]
            );

            const hoverItem = useCallback((itemId: string) => {
                setIsKeyboardActive(false);
                setActiveItemId(itemId);
            }, []);

            if (isLoading) {
                return (
                    <Stack id={id} role="group" gap="xs" p="md" aria-busy aria-label={label}>
                        <Skeleton h={14} w="40%" />
                        {Array.from({ length: GROUPED_LIST_LOADING_ROW_COUNT }, (_, index) => (
                            <Stack key={index} gap="xs" py="xs">
                                <Skeleton h={18} w="60%" />
                                <Skeleton h={14} w="90%" />
                            </Stack>
                        ))}
                    </Stack>
                );
            }

            if (groups.length === 0) {
                return (
                    <Text id={id} size="sm" dimmed px="md" py="md">
                        {emptyMessage}
                    </Text>
                );
            }

            return (
                <ScrollArea.Autosize className={styles.list} type="auto">
                    <Stack id={id} role="group" gap={0} aria-label={label}>
                        {groups.map((group, groupIndex) => {
                            const isCollapsed = collapsedGroupIds.has(group.id);
                            const headerId = getGroupHeaderId(id, group.id);
                            const listboxId = `${headerId}-options`;
                            return (
                                <Fragment key={group.id}>
                                    {groupIndex > 0 && <Divider my="xs" />}
                                    <GroupedListGroupHeader
                                        id={headerId}
                                        listboxId={listboxId}
                                        groupId={group.id}
                                        label={group.label}
                                        count={group.items.length}
                                        isCollapsed={isCollapsed}
                                        isFocusable={isFocusable}
                                        onToggle={toggleGroup}
                                    />
                                    <Collapse in={!isCollapsed}>
                                        <Stack id={listboxId} gap={0} role="listbox" aria-labelledby={headerId}>
                                            {group.items.map((item) => {
                                                const itemId = getItemId(item);
                                                const isActive =
                                                    typeof activeItem !== "undefined" &&
                                                    getItemId(activeItem) === itemId;
                                                return (
                                                    <GroupedListOption
                                                        key={itemId}
                                                        id={getOptionId(id, itemId)}
                                                        value={itemId}
                                                        {...getOptionContent(item)}
                                                        isActive={isActive}
                                                        isKeyboardActive={isKeyboardActive}
                                                        onChoose={chooseItem}
                                                        onHover={hoverItem}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </Collapse>
                                </Fragment>
                            );
                        })}
                    </Stack>
                </ScrollArea.Autosize>
            );
        }
    );

    GroupedListComponent.GroupHeader = GroupedListGroupHeader;
    GroupedListComponent.Option = GroupedListOption;
    return GroupedListComponent;
}

// The default, untyped GroupedList. For typed items, create one: `const TemplateList = createGroupedList<Template>();`.
const GroupedList = createGroupedList();

export { GroupedList, createGroupedList };
